import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Card, CardBody, CardHeader } from './ui/Card'

export const INVOICE_ONBOARDING_HREF = '/rules#invoice-onboarding'

const RULES: { title: string; text: string }[] = [
  {
    title: 'A new invoice that is not yet due',
    text: 'It follows your steps on their days, as normal.',
  },
  {
    title: 'An invoice that is already overdue when it is loaded',
    text: 'The customer gets one RemindLoop introduction first, then your normal steps. If your next step can go within one working day, we skip the introduction and send that step. The exception is the final notice: the introduction still goes first.',
  },
  {
    title: 'A step falls on a weekend or holiday, or sending is paused',
    text: 'At the next allowed time we send the best step for how old the invoice is now. Older missed steps are dropped. If a step can go within one working day, we wait for it. A step that is a few days behind still goes.',
  },
  {
    title: 'The final notice',
    text: 'It is never the first message. Legal referral only comes after it, or if it cannot be delivered.',
  },
  {
    title: 'The daily limit',
    text: 'At most one automatic message to a customer a day.',
  },
]

const EXAMPLE: string[] = [
  'Wed, 3 days before: the first reminder goes.',
  'Sat, due day, and Sun, 1 day late: nothing is sent on a weekend.',
  'Mon, 2 days late: the "due today" step is dropped, because it would no longer be true. The 1-day overdue step goes, a day behind. The next step is days away, so it does not wait.',
  'Sat, 7 days late: the 7-day step falls on a weekend.',
  'Mon, 9 days late: the 7-day step goes, 2 days behind. That is fine.',
  'The final notice goes only after an overdue step has been sent.',
]

function Diagram() {
  const box = 'fill-card stroke-border'
  const accent = 'fill-accent stroke-accent-foreground'
  const text = 'fill-foreground'
  const muted = 'fill-muted-foreground'
  return (
    <svg
      viewBox="0 0 320 420"
      role="img"
      aria-labelledby="onb-title onb-desc"
      className="mx-auto block h-auto w-full max-w-md"
      fontSize="13"
    >
      <title id="onb-title">How invoice onboarding works</title>
      <desc id="onb-desc">
        An invoice that is not yet due follows the normal steps. An invoice that is already overdue
        gets one introduction first, then the normal steps. On every send: a step on a weekend or
        holiday moves to the next allowed time, the final notice is never first, and a customer
        gets at most one automatic message a day.
      </desc>
      <g strokeWidth="1">
        <rect x="90" y="4" width="140" height="34" rx="2" className={box} />
        <text x="160" y="25" textAnchor="middle" className={text}>
          Invoice is loaded
        </text>

        <path d="M160 38 V52 M160 52 H75 V66 M160 52 H245 V66" fill="none" className="stroke-border" strokeWidth="1.5" />

        <rect x="8" y="66" width="134" height="56" rx="2" className={box} />
        <text x="75" y="86" textAnchor="middle" className={muted} fontSize="12">
          Not yet due
        </text>
        <text x="75" y="106" textAnchor="middle" className={text}>
          Normal steps
        </text>

        <rect x="178" y="66" width="134" height="56" rx="2" className={accent} />
        <text x="245" y="86" textAnchor="middle" className={muted} fontSize="12">
          Already overdue
        </text>
        <text x="245" y="106" textAnchor="middle" className={text}>
          One introduction
        </text>

        <path d="M245 122 V140" fill="none" className="stroke-border" strokeWidth="1.5" />
        <rect x="178" y="140" width="134" height="34" rx="2" className={box} />
        <text x="245" y="161" textAnchor="middle" className={text}>
          Then normal steps
        </text>
        <text x="312" y="192" textAnchor="end" className={muted} fontSize="12">
          Intro skipped if a normal step
        </text>
        <text x="312" y="207" textAnchor="end" className={muted} fontSize="12">
          can go within 1 working day
        </text>

        <path d="M75 122 V228 M245 214 V228 M75 228 H245" fill="none" className="stroke-border" strokeWidth="1.5" />
        <path d="M160 228 V240" fill="none" className="stroke-border" strokeWidth="1.5" />

        <text x="160" y="256" textAnchor="middle" className={muted} fontSize="12" letterSpacing="1.5">
          ON EVERY SEND
        </text>
        <rect x="8" y="264" width="304" height="46" rx="2" className={box} />
        <text x="160" y="283" textAnchor="middle" className={text}>
          Weekend, holiday or paused:
        </text>
        <text x="160" y="299" textAnchor="middle" className={text}>
          send the best step for its age later
        </text>
        <rect x="8" y="318" width="304" height="46" rx="2" className={box} />
        <text x="160" y="337" textAnchor="middle" className={text}>
          Final notice is never the first message
        </text>
        <text x="160" y="353" textAnchor="middle" className={text}>
          Legal referral only comes after it
        </text>
        <rect x="8" y="372" width="304" height="34" rx="2" className={box} />
        <text x="160" y="393" textAnchor="middle" className={text}>
          At most one message a customer a day
        </text>
      </g>
    </svg>
  )
}

export function InvoiceOnboarding() {
  const { hash } = useLocation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (hash !== '#invoice-onboarding') return
    setOpen(true)
    const t = setTimeout(() => ref.current?.scrollIntoView(), 300)
    return () => clearTimeout(t)
  }, [hash])

  return (
    <div id="invoice-onboarding" ref={ref} className="scroll-mt-6" data-testid="invoice-onboarding">
      <Card>
        <CardHeader title="How invoice onboarding works" />
        <CardBody>
          <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
            <summary className="cursor-pointer text-sm font-medium text-brand-700 hover:underline max-md:flex max-md:min-h-11 max-md:items-center dark:text-accent-foreground">
              {open ? 'Hide the explanation' : 'Show how new invoices are chased'}
            </summary>
            <div className="mt-4 space-y-5">
              <Diagram />
              <ol className="space-y-3 text-sm text-foreground">
                {RULES.map((r, i) => (
                  <li key={r.title} className="flex gap-3">
                    <span
                      aria-hidden="true"
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground"
                    >
                      {i + 1}
                    </span>
                    <span>
                      <strong className="font-semibold">{r.title}.</strong>{' '}
                      <span className="text-muted-foreground">{r.text}</span>
                    </span>
                  </li>
                ))}
              </ol>
              <div data-testid="onboarding-example">
                <h3 className="text-kicker font-semibold uppercase text-muted-foreground">
                  Example: an invoice due on a Saturday
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  The steps and days are an example. Yours may differ.
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-foreground">
                  {EXAMPLE.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </div>
          </details>
        </CardBody>
      </Card>
    </div>
  )
}
